import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ExternalLink, ShieldCheck } from "lucide-react";
import BrandHeader from "@/components/BrandHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/i18n";
import {
  clearCommercialSession,
  createCommercialOrder,
  getCommercialOrderStatus,
  getOrCreateCommercialIdempotencyKey,
  hasPaidEntitlement,
  hasPaidReportAccess,
  persistCommercialCheckout,
  readCommercialAccessToken,
  readCommercialIdempotencyKey,
  readCommercialCheckoutUrl,
  readCommercialOrderId,
  startNewCommercialCheckoutIntent,
} from "@/lib/commercialAccess";

const RECOVERABLE_ORDER_STATES = new Set(["FAILED", "CANCELLED"]);

export default function Checkout() {
  const { t } = useI18n();
  const [customer, setCustomer] = useState({ name: "", email: "" });
  const [orderId, setOrderId] = useState(() => readCommercialOrderId());
  const [accessToken, setAccessToken] = useState(() => readCommercialAccessToken());
  const [checkoutUrl, setCheckoutUrl] = useState(() => readCommercialCheckoutUrl());
  const [orderStatus, setOrderStatus] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState(() => {
    const existing = readCommercialIdempotencyKey();
    if (existing) return existing;

    // Existing R1.1 authority without a persisted intent must not silently mint
    // a new idempotency key. Fresh sessions persist an intent before any POST.
    if (readCommercialOrderId()) return null;
    return getOrCreateCommercialIdempotencyKey();
  });

  const clearStaleCommercialAuthority = useCallback(() => {
    clearCommercialSession();
    setOrderId(null);
    setAccessToken(null);
    setCheckoutUrl(null);
    setOrderStatus(null);
  }, []);

  const resetCommercialCheckout = useCallback(() => {
    clearCommercialSession();
    setOrderId(null);
    setAccessToken(null);
    setCheckoutUrl(null);
    setOrderStatus(null);
    setError("");
    setIdempotencyKey(startNewCommercialCheckoutIntent());
  }, []);

  const refreshStatus = useCallback(async () => {
    if (!orderId || !accessToken) return;
    try {
      const next = await getCommercialOrderStatus(orderId, accessToken);
      setOrderStatus(next);
      setError("");
    } catch (err) {
      if (err && err.status === 404) {
        clearStaleCommercialAuthority();
        return;
      }
      setError(t("commercial.statusError"));
    }
  }, [accessToken, clearStaleCommercialAuthority, orderId, t]);

  useEffect(() => {
    if (orderId && !accessToken) {
      clearStaleCommercialAuthority();
      return undefined;
    }
    if (!orderId || !accessToken) return undefined;

    refreshStatus();
    const timer = window.setInterval(refreshStatus, 3000);
    return () => window.clearInterval(timer);
  }, [accessToken, clearStaleCommercialAuthority, orderId, refreshStatus]);

  const createOrder = async (event) => {
    event.preventDefault();
    setCreating(true);
    setError("");

    if (!idempotencyKey) {
      // Fail closed: never mint a replacement key at submit time. A fresh
      // checkout intent must already have been durably persisted before POST.
      setError(t("commercial.createError"));
      setCreating(false);
      return;
    }

    try {
      const result = await createCommercialOrder({
        name: customer.name,
        email: customer.email,
        idempotencyKey,
      });
      persistCommercialCheckout({
        orderId: result.order_id,
        accessToken: result.access_token,
        checkoutUrl: result.checkout_url,
      });
      setOrderId(result.order_id);
      setAccessToken(result.access_token);
      setCheckoutUrl(result.checkout_url);
      setOrderStatus({
        order_id: result.order_id,
        status: result.status,
        entitlement_status: null,
      });
    } catch (err) {
      const body = err && err.body;
      if (
        err && err.status === 409 &&
        body &&
        ["BILL_CREATION_UNKNOWN", "FAILED"].includes(body.status) &&
        body.order_id &&
        body.access_token
      ) {
        persistCommercialCheckout({
          orderId: body.order_id,
          accessToken: body.access_token,
          checkoutUrl: null,
        });
        setOrderId(body.order_id);
        setAccessToken(body.access_token);
        setCheckoutUrl(null);
        setOrderStatus({
          order_id: body.order_id,
          status: body.status,
          entitlement_status: null,
        });
        setError("");
      } else {
        setError(err && err.message ? err.message : t("commercial.createError"));
      }
    } finally {
      setCreating(false);
    }
  };

  const paid = hasPaidEntitlement(orderStatus);
  const reportAccessible = hasPaidReportAccess(orderStatus);
  const consumed = reportAccessible && orderStatus.entitlement_status === "CONSUMED";
  const recoverable = !!orderStatus && RECOVERABLE_ORDER_STATES.has(orderStatus.status);
  const ambiguous = orderStatus && orderStatus.status === "BILL_CREATION_UNKNOWN";

  return (
    <div className="min-h-screen bg-navy-900 relative" data-testid="commercial-checkout-page">
      <div className="pointer-events-none absolute inset-0 opacity-40 bg-watermark-compass" />
      <div className="relative z-10">
        <BrandHeader />
        <main className="max-w-3xl mx-auto px-6 py-12 lg:py-16">
          <div className="card-premium corner-frame rounded-3xl p-7 sm:p-9">
            <div className="text-[10px] tracking-[0.35em] uppercase font-mono text-gold-400/80">
              {t("commercial.eyebrow")}
            </div>
            <h1 className="mt-3 font-serif-cn text-3xl text-gold-100">
              {t("commercial.title")}
            </h1>
            <p className="mt-4 text-sm text-slate-300 leading-relaxed">
              {t("commercial.description")}
            </p>

            {!orderId ? (
              <form className="mt-8 space-y-5" onSubmit={createOrder}>
                <div>
                  <Label htmlFor="commercial-name">{t("commercial.name")}</Label>
                  <Input
                    id="commercial-name"
                    required
                    maxLength={255}
                    value={customer.name}
                    onChange={(e) => setCustomer((p) => ({ ...p, name: e.target.value }))}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="commercial-email">{t("commercial.email")}</Label>
                  <Input
                    id="commercial-email"
                    type="email"
                    required
                    value={customer.email}
                    onChange={(e) => setCustomer((p) => ({ ...p, email: e.target.value }))}
                    className="mt-2"
                  />
                </div>
                <button
                  type="submit"
                  disabled={creating}
                  className="btn-gold rounded-full px-7 py-3 inline-flex items-center gap-2 text-sm disabled:opacity-50"
                  data-testid="commercial-create-order"
                >
                  {creating ? t("commercial.creating") : t("commercial.createCheckout")}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            ) : consumed ? (
              <div className="mt-8 rounded-2xl border border-emerald-400/35 bg-emerald-500/10 p-6">
                <div className="flex items-center gap-3 text-emerald-100">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">{t("dashboard.reportReady")}</span>
                </div>
                <Link to="/dashboard" className="mt-5 inline-block">
                  <button className="btn-gold rounded-full px-7 py-3 inline-flex items-center gap-2 text-sm">
                    {t("dashboard.title")}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
              </div>
            ) : paid ? (
              <div className="mt-8 rounded-2xl border border-emerald-400/35 bg-emerald-500/10 p-6">
                <div className="flex items-center gap-3 text-emerald-100">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">{t("commercial.paymentConfirmed")}</span>
                </div>
                <p className="mt-3 text-sm text-slate-300">
                  {t("commercial.entitlementActive")}
                </p>
                <Link to="/form" className="mt-5 inline-block">
                  <button className="btn-gold rounded-full px-7 py-3 inline-flex items-center gap-2 text-sm">
                    {t("commercial.continueToProfile")}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
              </div>
            ) : (
              <div className="mt-8 space-y-5">
                <div className="rounded-2xl border border-gold-500/25 bg-navy-800/60 p-5">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-5 h-5 text-gold-300" />
                    <div>
                      <div className="text-sm text-gold-100">
                        {recoverable ? t("commercial.checkoutEnded") : t("commercial.awaitingPayment")}
                      </div>
                      <div className="mt-1 text-xs text-slate-400">
                        {orderStatus ? orderStatus.status : t("commercial.checkingStatus")}
                      </div>
                    </div>
                  </div>
                </div>

                {ambiguous ? (
                  <p className="text-sm text-amber-200" data-testid="commercial-ambiguous-warning">
                    {t("commercial.ambiguousWarning")}
                  </p>
                ) : null}

                {!recoverable && !ambiguous && checkoutUrl ? (
                  <button
                    type="button"
                    className="btn-gold rounded-full px-7 py-3 inline-flex items-center gap-2 text-sm"
                    onClick={() => window.open(checkoutUrl, "_blank", "noopener,noreferrer")}
                    data-testid="commercial-open-payment"
                  >
                    {t("commercial.openPayment")}
                    <ExternalLink className="w-4 h-4" />
                  </button>
                ) : null}

                {recoverable ? (
                  <button
                    type="button"
                    className="btn-gold rounded-full px-7 py-3 text-sm"
                    onClick={resetCommercialCheckout}
                    data-testid="commercial-start-new-checkout"
                  >
                    {t("commercial.startNewCheckout")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-ghost-gold rounded-full px-6 py-3 text-sm"
                    onClick={refreshStatus}
                  >
                    {t("commercial.checkAgain")}
                  </button>
                )}
              </div>
            )}

            {error ? (
              <div className="mt-5 text-sm text-rose-300" role="alert">{error}</div>
            ) : null}

            <div className="hairline-gold my-7" />
            <p className="text-xs text-slate-400 leading-relaxed">
              {t("commercial.authorityNote")}
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
